import React from 'react';
import UnitButtonBox from './UnitButtonBox';
import DataCanvas from './DataCanvas';
import error from './imagenotthere.png';
import PropTypes from 'prop-types';

/**
 * DataCanvasContainer handles preloading of images and sending the
 * image element that corresponds to the selected image product
 *  to the DataCanvas component.
 *
 * @reactProps {function} getSource - getSource from Main, returns the src for an img
 * @reactProps {function} handleUnitChange - from Main, update the selected unit on Main state
 * @reactProps {Object} units - the units object, tells us start, by, and the header
 * @reactProps {string} unitType - the unitType for the selected product
 * @reactProps {string} unitThreshold - the max unit value for this cycle (from the Cycle object)
 * @reactProps {string} product - the selected product
 * @reactProps {string} cycle - the selected cycle
 * @reactProps {string} date - the selected date
 * @reactProps {string} domainImg - an image element when Domain of the Day is on, null if not
 */
export default class DataCanvasContainer extends React.Component {
  state = {
    values: [],
    index: 0
  };

  /**
   * It is essential to preload images for speed of viewing.
   */
  componentDidMount() {
    this.preload();
  }

  /**
   * Only preload again if there is a change in:
   *  product - that is a whole new file name,
   *  cycle - that represents a new directory,
   *   or
   *  date - that also represents a new directory
   * @reactProps {Object} prevProps - to be compared to current props
   * @reactProps {Object} prevState - default param for this React lifecycle method. unused here.
   */
  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.product !== this.props.product ||
      prevProps.cycle !== this.props.cycle ||
      prevProps.date !== this.props.date
    ) {
      this.preload();
    }
  }

  /**
   * Modularize getting the value out of the units object given a key.
   * @param {string} property - a key in the units object
   * @return {string} - the value of the property key in the units object
   */
  getUnitData = property => {
    return this.props.units[this.props.unitType][property];
  };

  /**
   * Every given unit type increases by a set amount, stored in the
   * units object on props. Every given unit also has a max. With this
   * getValues determines how many unit buttons need to be rendered
   * and what those units should be. These values correspond to the units
   * on the file names for the image products.
   * @return {string[]} values - an array of the strings that will be unit button labels
   */
  getValues = () => {
    const start = Number(this.getUnitData('start'));
    // TODO: Add member_minutes to json file
    const by = this.props.product.includes('member')
      ? 15
      : Number(this.getUnitData('by'));
    const limit = Number(this.props.unitThreashold);

    const length = limit / by + 1 - start;
    const values =
      this.props.unitType === 'full'
        ? ['000']
        : Array.from({ length }).map((_, i) => {
            by === 1 ? (i += by) : (i *= by);
            return i < 10 ? '00' + i : i > 99 ? i : '0' + i;
          });
    return values;
  };

  /**
   *  Get the array of values for the units, map to Promises that fullfill
   *  Image elements on load with urls based on the unit values.
   *  Set values, array of images and the promises to state.
   */
  preload() {
    const values = this.getValues();
    const images = Array.from({ length: values.length });
    const preload = Promise.all(
      values
        .map(val => this.props.getSource(val))
        .map((url, i) => {
          return new Promise((fullfill, reject) => {
            let img = new Image();
            img.onload = fullfill;
            img.onerror = reject;
            img.src = url;
            images[i] = (
              <img
                src={url}
                alt="Forecast"
                onError={e => {
                  e.target.src = error;
                }}
              />
            );
          });
        })
    );
    this.setState({ values, preload, images });
  }

  /**
   * Update the index so the correct img element passes to DataCanvas
   */
  change = index => {
    this.setState({ index });
  };

  /**
   * renders UnitButtonBox and DataCanvas
   */
  render() {
    if (this.state.images) {
      return (
        <div className="data-canvas-container">
          <UnitButtonBox
            classname={'horizontal'}
            title={this.getUnitData('unitHeader')}
            buttonValues={this.getValues()}
            onEvent={this.props.handleUnitChange}
            change={this.change}
          />
          <DataCanvas
            img={this.props.domainImg || this.state.images[this.state.index]}
          />
        </div>
      );
    } else {
      return <div />;
    }
  }
}

DataCanvasContainer.propTypes = {
  getSource: PropTypes.func.isRequired,
  handleUnitChange: PropTypes.func.isRequired,
  units: PropTypes.object.isRequired,
  unitType: PropTypes.string,
  unitThreshold: PropTypes.string,
  product: PropTypes.string,
  cycle: PropTypes.string,
  date: PropTypes.string,
  domainImg: PropTypes.string
};



// WEBPACK FOOTER //
// ./src/DataCanvasContainer.js